/**
 * Normaliza texto do AI para blocos legíveis (parágrafos e listas).
 */
export function normalizeAiText(text) {
  return String(text || '')
    .replace(/\r\n/g, '\n')
    .trim()
    .replace(/(\S)\s+(\d+\.\s)/g, '$1\n\n$2')
    .replace(/(\S)\s+([-•]\s)/g, '$1\n\n$2');
}

function isNumberedLine(line) {
  return /^\d+\.\s/.test(line);
}

function isBulletLine(line) {
  return /^[-•]\s/.test(line);
}

function toParagraphBlock(lines) {
  if (lines.length > 1) {
    return { type: 'lines', lines };
  }
  return { type: 'p', text: lines[0] };
}

/**
 * Divide resposta do AI em blocos estruturados.
 * @returns {Array<{ type: 'p'|'ol'|'ul'|'lines', text?: string, items?: string[], lines?: string[] }>}
 */
export function parseAiMessageBlocks(text) {
  const normalized = normalizeAiText(text);
  if (!normalized) return [];

  const rawBlocks = normalized.split(/\n\n+/).filter(Boolean);
  const merged = [];
  let pendingNumbered = [];
  let pendingBullets = [];

  const flushNumbered = () => {
    if (!pendingNumbered.length) return;
    merged.push({ type: 'ol', items: [...pendingNumbered] });
    pendingNumbered = [];
  };

  const flushBullets = () => {
    if (!pendingBullets.length) return;
    merged.push({ type: 'ul', items: [...pendingBullets] });
    pendingBullets = [];
  };

  const flushLists = () => {
    flushNumbered();
    flushBullets();
  };

  for (const block of rawBlocks) {
    const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);

    if (lines.length > 1 && lines.every(isNumberedLine)) {
      flushLists();
      merged.push({
        type: 'ol',
        items: lines.map((line) => line.replace(/^\d+\.\s*/, '')),
      });
      continue;
    }

    if (lines.length > 1 && lines.every(isBulletLine)) {
      flushLists();
      merged.push({
        type: 'ul',
        items: lines.map((line) => line.replace(/^[-•]\s*/, '')),
      });
      continue;
    }

    if (lines.length === 1 && isNumberedLine(lines[0])) {
      flushBullets();
      pendingNumbered.push(lines[0].replace(/^\d+\.\s*/, ''));
      continue;
    }

    if (lines.length === 1 && isBulletLine(lines[0])) {
      flushNumbered();
      pendingBullets.push(lines[0].replace(/^[-•]\s*/, ''));
      continue;
    }

    flushLists();
    merged.push(toParagraphBlock(lines));
  }

  flushLists();
  return merged;
}

/**
 * Partes inline com negrito markdown (**texto**).
 */
export function splitInlineFormatting(text) {
  return String(text || '').split(/(\*\*[^*]+\*\*)/g).filter((part) => part.length > 0);
}
